// app/src/features/report/useReportWiring.js
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useBriefPrefetch } from "../../hooks/useBriefPrefetch.js";
import { useSession } from "../../hooks/useSession.js";
import { useActiveForecast } from "../../hooks/useActiveForecast.js";
import { rootEngine } from "../../lib/forecast/engines.js";
import { buildReportSnapshot } from "../../lib/report/reportSnapshot.js";
import { getCalibration } from "../../services/forecastApi.js";

// CALIBRATION_MODEL_ID points at the live forecast engine because that is what users are running
// when they submit a forecast. This single id stands in for a future per-engine lookup driven by
// the active forecast's `forecastEngine`.
export const CALIBRATION_MODEL_ID = "sooth_forecast_engine_v0";

/**
 * The report wiring's first point: the calibration poll (the snapshot the timeline's calibration
 * tooltip reads), the brief prefetch for the forecast on screen, and the report modal's open
 * state — whether it is open, which of its sections are, the snapshot it shows (state, set at
 * open before the modal mounts and cleared at close) and the two handlers that open and close it.
 *
 * `openReport` / `closeReport` are plain arrows, new every render: `openReport` reads `active`,
 * `displayActive` and `comparison` at call time through the latest render's closure.
 * `closeReportRef` is kept current in a layout effect (every commit), so the tour's document
 * click listener — attached in an effect, fired after commit — always calls the latest
 * `closeReport`.
 *
 * Not here: the Listen briefing and the pointer / prefetch reads (`useReportPointerWiring`, which
 * needs the timeline framing's `autoZoom`), and `reportCtx`, which stays an `AppInner` literal
 * (it reads the tour's and the mark moments' outputs). `authLoading`, `authEnabled` and `me` (`me.sub`
 * keys the poll) are read from the session context through `useSession()`.
 *
 * @param {object} deps
 * @param {object|null} deps.displayActive  the forecast as displayed (the reforecast scrub's)
 * @param {object|null} deps.comparison  the legacy comparison selection
 */
export function useReportWiring({ displayActive, comparison }) {
  const { authLoading, authEnabled, me } = useSession();
  const active = useActiveForecast();
  // null = not yet fetched or unavailable; the tooltip then hides the calibration line. Refetched
  // every 5 minutes so a long-lived tab picks up new Observatory rebuilds without a page reload.
  const [calibration, setCalibration] = useState(null);
  useEffect(() => {
    // Polls for every user, but only once authenticated, since /api/calibration sits behind /api
    // requireAuth and would just 401 otherwise. Legacy/no-Zitadel mode (authEnabled false) needs no
    // session, so it polls immediately; otherwise it waits for `me`. Keying on `me?.sub` re-runs
    // the effect the instant the user becomes authenticated (e.g. cross-tab sign-in picked up by
    // the visibilitychange /api/me refresh), and tears the interval down once `me` clears. A
    // session that expires while `me?.sub` is still set keeps polling and 401s harmlessly: the
    // snapshot is shared/non-sensitive, so the last-good value is kept (a stale line, not a cleared
    // one) until `/api/me` refreshes and clears `me`, which then stops this interval.
    if (authLoading) return;
    if (authEnabled && !me?.sub) return;
    let cancelled = false;
    const refresh = async () => {
      // Never throws (network/401 handled centrally). A transient non-ok result keeps the prior
      // snapshot; calibration switched off on the server clears it.
      const r = await getCalibration();
      if (!r.ok) {
        if (!cancelled && r.body?.error === "calibration_disabled") setCalibration(null);
        return;
      }
      if (!cancelled) setCalibration(r.body);
    };
    refresh();
    const id = setInterval(refresh, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [authLoading, authEnabled, me?.sub]);
  // A Mirabilis report's written brief lines start as soon as the forecast has its report.
  useBriefPrefetch({
    source: active,
    engine: active ? rootEngine(active) || null : null,
  });
  const [showReport, setShowReport] = useState(false);
  const [reportKeyFactorsOpen, setReportKeyFactorsOpen] = useState(false);
  const [reportResolutionOpen, setReportResolutionOpen] = useState(false);
  const [reportSourcesOpen, setReportSourcesOpen] = useState(false);
  // A single toggle expands the YES/NO factor lists together; only the top 5 are shown by default.
  const [expandReasons, setExpandReasons] = useState(false);
  const [reportAggregationOpen, setReportAggregationOpen] = useState(false);
  const [reportCalibrationOpen, setReportCalibrationOpen] = useState(false);
  const [reportSnapshot, setReportSnapshot] = useState(null);
  const openReport = (val) => {
    const snap = buildReportSnapshot(val, { active, displayActive, comparison });
    if (snap) {
      setReportSnapshot(snap);
      // The report modal reads the timeline scrub position (viewMomentIso) itself. Root/branch
      // opens carry their own snapshot and no scrub context (handled in the modal).
      setExpandReasons(false);
    }
    setShowReport(val);
  };
  const closeReport = () => {
    setReportSnapshot(null);
    setShowReport(false);
    setReportKeyFactorsOpen(false);
    setReportResolutionOpen(false);
    setReportSourcesOpen(false);
    setReportAggregationOpen(false);
    setReportCalibrationOpen(false);
    setExpandReasons(false);
  };
  // Stable handle to closeReport for the tour's global "click anywhere outside
  // the report closes it" listener (the chat panel is z-raised above the report
  // backdrop during the tour, so its own clicks wouldn't otherwise close it).
  const closeReportRef = useRef(closeReport);
  useLayoutEffect(() => {
    closeReportRef.current = closeReport;
  });
  return {
    calibration,
    showReport,
    openReport,
    closeReport,
    closeReportRef,
    reportSnapshot,
    reportKeyFactorsOpen,
    setReportKeyFactorsOpen,
    reportResolutionOpen,
    setReportResolutionOpen,
    reportSourcesOpen,
    setReportSourcesOpen,
    reportAggregationOpen,
    setReportAggregationOpen,
    reportCalibrationOpen,
    setReportCalibrationOpen,
    expandReasons,
    setExpandReasons,
  };
}
